/**
 * MitreMapping — MITRE ATT&CK technique table with needs_review flag.
 */

import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DataTable, type Column } from '../../../components/DataTable';
import { LoadingState } from '../../../components/LoadingState';
import { ErrorState } from '../../../components/ErrorState';
import { useAuth } from '../../../components/AuthProvider';
import { listDocuments } from '../../../api/documents';
import apiClient from '../../../api/client';
import type { MitreTechnique } from '../../../api/types';

export function MitreMapping() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const documentId = searchParams.get('doc') || '';
  const [techniques, setTechniques] = useState<MitreTechnique[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = async () => {
    if (!user) return;
    setLoading(true);
    setError(null);
    try {
      // If a specific document is provided, fetch its mappings
      // Otherwise try to get the first completed document
      let docId = documentId;
      if (!docId) {
        const docsResponse = await listDocuments(user.tenantId);
        const completedDocs = docsResponse.documents.filter(
          (d) => d.status === 'completed' || d.status === 'mapped' || d.status === 'indexed'
        );
        const completedDoc = completedDocs.length > 0 ? completedDocs[completedDocs.length - 1] : null;
        if (completedDoc) {
          docId = completedDoc.document_id;
        }
      }

      if (!docId) {
        setTechniques([]);
        setLoading(false);
        return;
      }

      const response = await apiClient.get(`/cti/workflow/${docId}`, {
        params: { tenant_id: user.tenantId, stage: 'mitre' },
      });
      setTechniques(response.data.techniques || []);
    } catch {
      setError('Failed to load MITRE mappings.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, documentId]);

  if (loading) return <LoadingState message="Loading MITRE mappings..." />;
  if (error) return <ErrorState message={error} onRetry={fetchData} />;

  if (techniques.length === 0) {
    return (
      <div className="space-y-8">
        <div>
          <h1 className="text-heading-lg font-bold text-slate-text">MITRE ATT&CK Mapping</h1>
          <p className="text-body text-slate-muted mt-1">
            Techniques identified from analyzed CTI documents
          </p>
        </div>
        <div className="glass-card text-center py-12">
          <p className="text-body text-slate-muted">
            No MITRE mappings yet. Upload and process a CTI document to see technique mappings.
          </p>
        </div>
      </div>
    );
  }

  const columns: Column<MitreTechnique>[] = [
    { key: 'technique_id', header: 'Technique ID', width: '140px' },
    { key: 'technique_name', header: 'Name' },
    { key: 'tactic', header: 'Tactic' },
    {
      key: 'confidence',
      header: 'Confidence',
      render: (row) => (
        <span className={`font-semibold ${row.confidence >= 0.8 ? 'text-green-700' : row.confidence >= 0.6 ? 'text-yellow-700' : 'text-red-700'}`}>
          {(row.confidence * 100).toFixed(0)}%
        </span>
      ),
      width: '120px',
    },
    {
      key: 'needs_review',
      header: 'Review',
      render: (row) =>
        row.needs_review ? (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-[14px] font-semibold bg-amber-100 text-amber-800">
            Needs Review
          </span>
        ) : (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-[14px] font-semibold bg-green-100 text-green-800">
            Confirmed
          </span>
        ),
      width: '140px',
    },
  ];

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-heading-lg font-bold text-slate-text">MITRE ATT&CK Mapping</h1>
        <p className="text-body text-slate-muted mt-1">
          Techniques identified from analyzed CTI documents
        </p>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="glass-card text-center">
          <p className="text-[16px] font-semibold text-slate-muted">Total Techniques</p>
          <p className="text-heading font-bold text-primary-teal">{techniques.length}</p>
        </div>
        <div className="glass-card text-center">
          <p className="text-[16px] font-semibold text-slate-muted">Confirmed</p>
          <p className="text-heading font-bold text-green-700">
            {techniques.filter((t) => !t.needs_review).length}
          </p>
        </div>
        <div className="glass-card text-center">
          <p className="text-[16px] font-semibold text-slate-muted">Needs Review</p>
          <p className="text-heading font-bold text-amber-700">
            {techniques.filter((t) => t.needs_review).length}
          </p>
        </div>
      </div>

      {/* Techniques table */}
      <DataTable
        columns={columns}
        data={techniques}
        keyExtractor={(row) => row.technique_id}
        emptyMessage="No MITRE techniques mapped yet."
      />

      {/* Evidence details */}
      <div className="glass-card">
        <h3 className="text-section-sm font-bold text-slate-text mb-4">Evidence Details</h3>
        <div className="space-y-4">
          {techniques.map((t) => (
            <div key={t.technique_id} className="border-b pb-3" style={{ borderColor: 'var(--color-table-border)' }}>
              <p className="font-semibold text-body text-slate-text">
                {t.technique_id} — {t.technique_name}
              </p>
              <p className="text-[16px] text-slate-muted mt-1">{t.evidence}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
